import React from 'react';
import { Tooltip } from '@material-ui/core';
import { _ } from '../../functions/translator.js';

const AnimatedProgressBar = ({ prevPercentage, postPercentage, targetPercentage, user_name, protocol_name, shouldAnimate }) => {
    const [currentPercentage, setCurrentPercentage] = React.useState(prevPercentage);
    const [isAnimating, setIsAnimating] = React.useState(false);
    const [hasAnimated, setHasAnimated] = React.useState(false);
    const progressBarRef = React.useRef(null);
    const isOverTarget = postPercentage > targetPercentage;
    
    React.useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !hasAnimated) {
                        setIsAnimating(true);
                        setHasAnimated(true);
                        // Delay para que la animación sea visible
                        const timer = setTimeout(() => {
                            setCurrentPercentage(postPercentage);
                        }, 300);
                        return () => clearTimeout(timer);
                    }
                });
            },
            { threshold: 0.5 } // Se activa cuando el 50% del elemento es visible
        );

        if (progressBarRef.current) {
            observer.observe(progressBarRef.current);
        }

        return () => {
            if (progressBarRef.current) {
                observer.unobserve(progressBarRef.current);
            }
        };
    }, [postPercentage, hasAnimated]);
    
    return (
        <div ref={progressBarRef} className="user-progress-card">
            {/* Header con nombre de usuario */}
            <div className="user-progress-header">
                <div>
                    <p className="user-name">
                        {user_name}
                    </p>
                </div>
            </div>

            {/* Barra de progreso animada */}
            <div className="progress-bar-container">
                <div className="progress-bar-track">
                    {/* Barra de progreso inicial (azul) */}
                    <div 
                        className="progress-bar-segment initial"
                        style={{ width: `${prevPercentage}%` }}
                    />
                    
                    {/* Barra de progreso del cambio (azul) - se pinta animadamente desde prev hasta post */}
                    <div 
                        className={`progress-bar-segment initial animated ${isAnimating ? 'animating' : ''}`}
                        style={{ 
                            left: `${prevPercentage}%`,
                            width: `${currentPercentage - prevPercentage}%`
                        }}
                    />
                    
                    {/* Línea vertical para porcentaje inicial */}
                    <div 
                        className="progress-bar-marker initial"
                        style={{ left: `${prevPercentage}%` }}
                    />
                    
                    {/* Línea blanca horizontal a la altura del prevPercentage */}
                    <div 
                        className="progress-bar-white-line"
                        style={{ 
                            left: `${prevPercentage}%`,
                            width: '5px',
                            height: '100%',
                            backgroundColor: 'white',
                            position: 'absolute',
                            top: '0',
                            zIndex: 2
                        }}
                    />
                    
                    {/* Línea vertical para porcentaje objetivo */}
                    <div 
                        className="progress-bar-marker target"
                        style={{ left: `${targetPercentage}%` }}
                    />
                    
                    {/* Tag del porcentaje anterior (debajo de la barra) */}
                    <Tooltip 
                        title={<span style={{ fontSize: '12px', cursor: 'pointer' }}>{_('Porcentaje de revisión anterior')}</span>} 
                        arrow>
                        <div 
                            className="progress-percentage-tag prev"
                            style={{ left: `${Math.max(5, Math.min(95, prevPercentage))}%` }}
                        >
                            {prevPercentage.toFixed(1)}%
                        </div>
                    </Tooltip>
                    
                    {/* Tag del porcentaje alcanzado (encima de la barra) */}
                    <Tooltip 
                        title={<span style={{ fontSize: '12px', cursor: 'pointer' }}>{_('Porcentaje de revisión actualizado')}</span>} 
                        arrow
                    >
                        <div 
                            className="progress-percentage-tag post"
                            style={{ 
                                left: `${Math.max(5, Math.min(95, postPercentage))}%`,
                                backgroundColor: '#1976d2', // Azul como la barra
                                color: 'white',
                                border: 'none'
                            }}
                        >
                            {postPercentage.toFixed(1)}%
                        </div>
                    </Tooltip>
                </div>
                
                {/* Texto "Porcentaje de revisión objetivo" - posicionado fijo en la esquina superior derecha */}
                <div className="progress-label target">
                    {_('Porcentaje de revisión objetivo')}: {targetPercentage.toFixed(1)}%
                </div>
                
                {/* Mensaje de estado dentro del contenedor */}
                {isOverTarget && (
                    <div className="over-target-message">
                        ⚠️ {_('Este usuario ha excedido su porcentaje objetivo de revisión. No se le asignarán más registros.')}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AnimatedProgressBar;
